import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X, Globe, User, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export const Navbar = () => {
  const { t, language, setLanguage, dir } = useLanguage();
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const languages = [
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  ];

  const navLinks = [
    { href: '/', label: t('nav.home') },
    { href: '/#services', label: t('nav.services') },
    { href: '/#gallery', label: t('nav.gallery') },
    { href: '/#contact', label: t('nav.contact') },
  ];

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'glass-nav shadow-lg' : 'bg-transparent'
      }`}
      dir={dir}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <span className="font-heading text-2xl md:text-3xl font-light tracking-tight text-text-primary">
              Nûr <span className="text-primary">Events</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="font-body text-sm font-medium text-text-primary hover:text-primary transition-colors duration-300"
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger
                className="flex items-center gap-2 px-3 py-2 text-sm font-body text-text-primary hover:text-primary transition-colors"
                data-testid="language-switcher"
              >
                <Globe className="w-4 h-4" />
                <span>{languages.find(l => l.code === language)?.flag}</span>
                <ChevronDown className="w-3 h-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white border border-primary/10">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className="cursor-pointer font-body hover:bg-primary/5"
                    data-testid={`lang-${lang.code}`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger
                  className="flex items-center gap-2 px-4 py-2 text-sm font-body text-text-primary hover:text-primary transition-colors"
                  data-testid="user-menu"
                >
                  <User className="w-4 h-4" />
                  <span>{user.name?.split(' ')[0]}</span>
                  <ChevronDown className="w-3 h-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white border border-primary/10">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="cursor-pointer font-body hover:bg-primary/5" data-testid="nav-dashboard">
                      {t('nav.dashboard')}
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="cursor-pointer font-body hover:bg-primary/5" data-testid="nav-admin">
                        {t('nav.admin')}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="cursor-pointer font-body hover:bg-primary/5 text-red-600"
                    data-testid="nav-logout"
                  >
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  to="/auth?mode=login"
                  className="px-4 py-2 text-sm font-body font-medium text-text-primary hover:text-primary transition-colors"
                  data-testid="nav-login"
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/booking"
                  className="btn-primary text-sm"
                  data-testid="nav-book"
                >
                  {t('nav.book')}
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-text-primary"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden py-6 space-y-4 bg-white/95 backdrop-blur-xl border-t border-primary/10">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setIsOpen(false)}
                className="block px-4 py-2 font-body text-text-primary hover:text-primary transition-colors"
              >
                {link.label}
              </a>
            ))}
            <div className="flex items-center gap-2 px-4 pt-4 border-t border-primary/10">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => { setLanguage(lang.code); setIsOpen(false); }}
                  className={`px-3 py-1 text-sm font-body rounded ${
                    language === lang.code ? 'bg-primary text-white' : 'bg-surface-light'
                  }`}
                >
                  {lang.flag}
                </button>
              ))}
            </div>
            {user ? (
              <div className="px-4 pt-4 space-y-2 border-t border-primary/10">
                <Link to="/dashboard" onClick={() => setIsOpen(false)} className="block py-2 font-body text-text-primary">
                  {t('nav.dashboard')}
                </Link>
                {isAdmin && (
                  <Link to="/admin" onClick={() => setIsOpen(false)} className="block py-2 font-body text-text-primary">
                    {t('nav.admin')}
                  </Link>
                )}
                <button onClick={() => { handleLogout(); setIsOpen(false); }} className="block py-2 font-body text-red-600">
                  {t('nav.logout')}
                </button>
              </div>
            ) : (
              <div className="px-4 pt-4 space-y-3 border-t border-primary/10">
                <Link
                  to="/auth?mode=login"
                  onClick={() => setIsOpen(false)}
                  className="block text-center py-3 font-body text-text-primary border border-primary/20"
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/booking"
                  onClick={() => setIsOpen(false)}
                  className="block text-center btn-primary"
                >
                  {t('nav.book')}
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};
